import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { getAdminSession } from '../../utils/adminSession';
import { amendPlayerUrl, entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { PlayerInfo } from '../../models/PlayerInfo';
import { PlayerStatsInterface } from '../../models/PlayerStats';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const FileInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin: 1.25rem 0 0.5rem;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const toTitleCase = (value: string): string => {
  if (!value) return value;

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const normalizePosition = (value: string): string => {
  if (!value) return value;
  return value
    .trim()
    .split('/')
    .map((part) => {
      const p = part.trim();
      if (!p) return p;
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join('/');
};

const parseNumber = (value: string): number => {
  const n = Number(value);
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return n;
};

const AmendPlayer: React.FC = () => {
  const session = getAdminSession();
  const apiKey = session?.apiKey ?? null;

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [stats, setStats] = useState<PlayerStatsInterface[]>([]);
  const [goalieStats, setGoalieStats] = useState<PlayerStatsInterface[]>([]);
  const [selectedName, setSelectedName] = useState('');

  const [formName, setFormName] = useState('');
  const [formPosition, setFormPosition] = useState('');
  const [formNumber, setFormNumber] = useState('0');
  const [formShoots, setFormShoots] = useState('');
  const [formSponsor, setFormSponsor] = useState('');
  const [formSponsorLink, setFormSponsorLink] = useState('');
  const [formSponsorImg, setFormSponsorImg] = useState('');
  const [formFunFact, setFormFunFact] = useState('');

  const [gamesPlayed, setGamesPlayed] = useState('0');
  const [goals, setGoals] = useState('0');
  const [assists, setAssists] = useState('0');
  const [pims, setPims] = useState('0');

  const [wins, setWins] = useState('0');
  const [losses, setLosses] = useState('0');
  const [so, setSo] = useState('0');
  const [ga, setGa] = useState('0');
  const [saves, setSaves] = useState('0');
  const [shotsAgainst, setShotsAgainst] = useState('0');

  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [isReadingImage, setIsReadingImage] = useState(false);
  const [playerImageError, setPlayerImageError] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);

  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  useEffect(() => {
    if (!apiKey) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);

        const CACHE_TTL_MS = 5 * 60 * 1000;
        const [playersItems, statsItems, goalieStatsItems] = await Promise.all([
          fetchItemsWithCache<PlayerInfo>(
            entriesUrl('tigers_players'),
            CACHE_KEYS.players,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_stats'),
            CACHE_KEYS.stats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
        ]);

        const sortedPlayers = [...(playersItems ?? [])].sort((a, b) => a.name.localeCompare(b.name));
        setPlayers(sortedPlayers);
        setStats(statsItems ?? []);
        setGoalieStats(goalieStatsItems ?? []);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load player data.';
        setErrorMessage(message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [apiKey]);

  const selectedPlayer = useMemo(() => players.find((p) => p.name === selectedName) ?? null, [players, selectedName]);

  const selectedPlayerIsGoalie = useMemo(() => {
    return selectedPlayer?.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;
  }, [selectedPlayer]);

  const selectedStatsItem = useMemo(() => {
    if (!selectedName) return null;

    const isGoalie = selectedPlayerIsGoalie;
    if (isGoalie) {
      return goalieStats.find((s) => s.player === selectedName) ?? null;
    }

    return stats.find((s) => s.player === selectedName) ?? null;
  }, [selectedName, selectedPlayerIsGoalie, stats, goalieStats]);

  useEffect(() => {
    if (!selectedPlayer || !selectedStatsItem) return;

    setFormName(selectedPlayer.name);
    setFormPosition(selectedPlayer.position);
    setFormNumber(String(selectedPlayer.number ?? 0));
    setFormShoots(selectedPlayer.shoots ?? '');
    setFormSponsor(selectedPlayer.sponsor ?? '');
    setFormSponsorLink(selectedPlayer.sponsorLink ?? '');
    setFormSponsorImg(selectedPlayer.sponsorImg ?? '');
    setFormFunFact(selectedPlayer.funfact ?? '');

    if (selectedPlayerIsGoalie) {
      setGamesPlayed(String(selectedStatsItem.gamesPlayed ?? 0));
      setWins(String((selectedStatsItem as any).wins ?? 0));
      setLosses(String((selectedStatsItem as any).losses ?? 0));
      setSo(String((selectedStatsItem as any).so ?? 0));
      setGa(String((selectedStatsItem as any).ga ?? 0));
      setSaves(String((selectedStatsItem as any).saves ?? 0));
      setShotsAgainst(String((selectedStatsItem as any).shotsAgainst ?? 0));
    } else {
      setGamesPlayed(String(selectedStatsItem.gamesPlayed ?? 0));
      setGoals(String((selectedStatsItem as any).goals ?? 0));
      setAssists(String((selectedStatsItem as any).assists ?? 0));
      setPims(String((selectedStatsItem as any).pims ?? 0));
    }

    setImageBase64(null);
    setImageFileName(null);
    setPlayerImageError(null);
    setIsReadingImage(false);
    setImageInputKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer?.name, selectedPlayerIsGoalie]);

  const computedSkater = useMemo(() => {
    const gp = parseNumber(gamesPlayed);
    const g = parseNumber(goals);
    const a = parseNumber(assists);
    const pim = parseNumber(pims);
    const points = g + a;
    const ppg = gp > 0 ? points / gp : 0;
    return { gp, g, a, pim, points, ppg };
  }, [gamesPlayed, goals, assists, pims]);

  const computedGoalie = useMemo(() => {
    const gp = parseNumber(gamesPlayed);
    const w = parseNumber(wins);
    const l = parseNumber(losses);
    const shutouts = parseNumber(so);
    const gaNum = parseNumber(ga);
    const saved = parseNumber(saves);
    const shots = parseNumber(shotsAgainst);
    const gaa = gp > 0 ? gaNum / gp : 0;
    const svPercent = saved + shots > 0 ? (saved / (saved + shots)) * 100 : 0;
    return { gp, w, l, shutouts, ga: gaNum, gaa, saved, shots, svPercent };
  }, [gamesPlayed, wins, losses, so, ga, saves, shotsAgainst]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handlePlayerImageFile = (file: File | null) => {
    setPlayerImageError(null);

    if (!file) {
      setImageBase64(null);
      setImageFileName(null);
      setIsReadingImage(false);
      return;
    }

    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) {
      setPlayerImageError('Player image must be a PNG file.');
      return;
    }

    const maxBytes = 400 * 1024;
    if (file.size > maxBytes) {
      setPlayerImageError('Player image must be 400KB or smaller.');
      return;
    }

    const reader = new FileReader();
    setIsReadingImage(true);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      // data:[mime];base64,XXXXX -> take only XXXXX
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setImageBase64(base64);
      setImageFileName(file.name);
      setIsReadingImage(false);
    };
    reader.onerror = () => {
      setPlayerImageError('Unable to read selected image file.');
      setIsReadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const resetAll = () => {
    setSelectedName('');
    setFormName('');
    setFormPosition('');
    setFormNumber('0');
    setFormShoots('');
    setFormSponsor('');
    setFormSponsorLink('');
    setFormSponsorImg('');
    setFormFunFact('');
    setGamesPlayed('0');
    setGoals('0');
    setAssists('0');
    setPims('0');
    setWins('0');
    setLosses('0');
    setSo('0');
    setGa('0');
    setSaves('0');
    setShotsAgainst('0');
    setImageBase64(null);
    setImageFileName(null);
    setPlayerImageError(null);
    setIsReadingImage(false);
    setImageInputKey((k) => k + 1);
    setSubmitState('idle');
    setErrorMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!selectedPlayer) return;
    if (isReadingImage) {
      setErrorMessage('Please wait for the image to finish loading.');
      return;
    }
    if (playerImageError) {
      setErrorMessage(playerImageError);
      return;
    }
    if (!formName.trim() || !formPosition.trim()) {
      setErrorMessage('Name and position are required.');
      return;
    }

    setSubmitState('submitting');

    try {
      const payload = {
        tableName: 'tigers_players',
        teamName: 'tigers',
        currentName: selectedPlayer.name,
        name: toTitleCase(formName),
        position: normalizePosition(formPosition),
        number: parseNumber(formNumber),
        shoots: formShoots || undefined,
        sponsor: formSponsor || undefined,
        sponsorLink: formSponsorLink || undefined,
        sponsorImg: formSponsorImg || undefined,
        funfact: formFunFact || undefined,
        ...(imageBase64 !== null ? { imageBase64 } : {}),
        ...(selectedPlayerIsGoalie
          ? {
              gamesPlayed: computedGoalie.gp,
              wins: computedGoalie.w,
              losses: computedGoalie.l,
              so: computedGoalie.shutouts,
              ga: computedGoalie.ga,
              gaa: computedGoalie.gaa,
              saves: computedGoalie.saved,
              shotsAgainst: computedGoalie.shots,
              SVPercent: computedGoalie.svPercent,
            }
          : {
              gamesPlayed: computedSkater.gp,
              goals: computedSkater.g,
              assists: computedSkater.a,
              points: computedSkater.points,
              ppg: computedSkater.ppg,
              pims: computedSkater.pim,
            }),
      };

      const response = await fetch(amendPlayerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': session.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to update player');
      }

      localStorage.removeItem(CACHE_KEYS.players);
      localStorage.removeItem(CACHE_KEYS.stats);
      localStorage.removeItem(CACHE_KEYS.goalieStats);

      setSubmitState('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while updating player';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Title>Amend Player</Title>
        <div>Loading player data…</div>
      </PageContainer>
    );
  }

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Player Updated</Title>
        <Message>The player details were updated successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetAll}>
            Amend another player
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Player Not Updated</Title>
        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetAll}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Amend Player</Title>
      {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label htmlFor="playerSelect">
            Player<RequiredMark>*</RequiredMark>
          </Label>
          <Select
            id="playerSelect"
            value={selectedName}
            onChange={(e) => setSelectedName(e.target.value)}
            required
          >
            <option value="">Select a player</option>
            {players.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </Select>
          {!selectedName ? <HelperText>Select a player to edit.</HelperText> : null}
        </FieldGroup>

        {selectedPlayer ? (
          <>
            <SectionTitle>Player Details</SectionTitle>

            <FieldGroup>
              <Label htmlFor="name">
                Name<RequiredMark>*</RequiredMark>
              </Label>
              <Input id="name" type="text" value={formName} onChange={(e) => setFormName(e.target.value)} required />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="position">
                Position<RequiredMark>*</RequiredMark>
              </Label>
              <Select
                id="position"
                value={formPosition}
                onChange={(e) => setFormPosition(e.target.value)}
                required
              >
                <option value="">Select position</option>
                <option value="Forward">Forward</option>
                <option value="Defense">Defense</option>
                <option value="Forward/Defense">Forward/Defense</option>
                <option value="Goalie">Goalie</option>
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="number">
                Number<RequiredMark>*</RequiredMark>
              </Label>
              <Input
                id="number"
                type="number"
                min="0"
                value={formNumber}
                onChange={(e) => setFormNumber(e.target.value)}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="shoots">Shoots (L/R)</Label>
              <Select id="shoots" value={formShoots} onChange={(e) => setFormShoots(e.target.value)}>
                <option value="">Not set</option>
                <option value="Left">L</option>
                <option value="Right">R</option>
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorName">Sponsor Name</Label>
              <Input id="sponsorName" type="text" value={formSponsor} onChange={(e) => setFormSponsor(e.target.value)} />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorUrl">Sponsor URL</Label>
              <Input
                id="sponsorUrl"
                type="url"
                value={formSponsorLink}
                onChange={(e) => setFormSponsorLink(e.target.value)}
                placeholder="https://example.com"
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="sponsorImageUrl">Sponsor Image URL</Label>
              <Input
                id="sponsorImageUrl"
                type="url"
                value={formSponsorImg}
                onChange={(e) => setFormSponsorImg(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="funFact">Fun Fact</Label>
              <Input id="funFact" type="text" value={formFunFact} onChange={(e) => setFormFunFact(e.target.value)} />
            </FieldGroup>

            <SectionTitle>Player Image (optional)</SectionTitle>
            <FieldGroup>
              <Label htmlFor="playerImage">Player Image (PNG, max 400KB)</Label>
              <FileInput
                key={imageInputKey}
                id="playerImage"
                type="file"
                accept="image/png"
                onChange={(e) => handlePlayerImageFile(e.target.files ? e.target.files[0] : null)}
              />
              {playerImageError ? <HelperText style={{ color: '#ffd1d1' }}>{playerImageError}</HelperText> : null}
              {imageFileName ? (
                <HelperText>
                  Selected: <span style={{ color: '#c9f7d6' }}>{imageFileName}</span>
                </HelperText>
              ) : null}
              {isReadingImage ? <HelperText style={{ marginTop: '0.5rem' }}>Reading image…</HelperText> : null}

              {imageBase64 ? (
                <ActionsRow>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      setImageBase64(null);
                      setImageFileName(null);
                      setPlayerImageError(null);
                      setIsReadingImage(false);
                      setImageInputKey((k) => k + 1);
                    }}
                  >
                    Remove image
                  </SecondaryButton>
                </ActionsRow>
              ) : null}
            </FieldGroup>

            <SectionTitle>Season Stats</SectionTitle>

            {selectedPlayerIsGoalie ? (
              <>
                <FieldGroup>
                  <Label htmlFor="gamesPlayed">Games Played</Label>
                  <Input id="gamesPlayed" type="number" min="0" value={gamesPlayed} onChange={(e) => setGamesPlayed(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="wins">Wins</Label>
                  <Input id="wins" type="number" min="0" value={wins} onChange={(e) => setWins(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="losses">Loses</Label>
                  <Input id="losses" type="number" min="0" value={losses} onChange={(e) => setLosses(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="so">SO</Label>
                  <Input id="so" type="number" min="0" value={so} onChange={(e) => setSo(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="ga">GA</Label>
                  <Input id="ga" type="number" min="0" value={ga} onChange={(e) => setGa(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="gaa">GAA (GA / GP)</Label>
                  <Input id="gaa" type="number" step="0.01" min="0" value={String(computedGoalie.gaa)} readOnly />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="saves">Saves</Label>
                  <Input id="saves" type="number" min="0" value={saves} onChange={(e) => setSaves(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="shotsAgainst">Shots Against</Label>
                  <Input
                    id="shotsAgainst"
                    type="number"
                    min="0"
                    value={shotsAgainst}
                    onChange={(e) => setShotsAgainst(e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="svPercent">SV% (Saves / (Saves + Shots))</Label>
                  <Input id="svPercent" type="number" step="0.01" min="0" value={String(computedGoalie.svPercent)} readOnly />
                </FieldGroup>
              </>
            ) : (
              <>
                <FieldGroup>
                  <Label htmlFor="gamesPlayedSkater">Games Played</Label>
                  <Input
                    id="gamesPlayedSkater"
                    type="number"
                    min="0"
                    value={gamesPlayed}
                    onChange={(e) => setGamesPlayed(e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="goals">Goals</Label>
                  <Input id="goals" type="number" min="0" value={goals} onChange={(e) => setGoals(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="assists">Assists</Label>
                  <Input id="assists" type="number" min="0" value={assists} onChange={(e) => setAssists(e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="points">Points (G + A)</Label>
                  <Input id="points" type="number" min="0" value={String(computedSkater.points)} readOnly />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="ppg">PPG (Points / GP)</Label>
                  <Input id="ppg" type="number" step="0.01" min="0" value={String(computedSkater.ppg)} readOnly />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="pims">PIMs</Label>
                  <Input id="pims" type="number" min="0" value={pims} onChange={(e) => setPims(e.target.value)} />
                </FieldGroup>
              </>
            )}
          </>
        ) : (
          <HelperText>Select a player to load stats.</HelperText>
        )}

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
          <SubmitButton type="submit" disabled={!selectedPlayer || submitState === 'submitting' || isReadingImage || !!playerImageError}>
            {submitState === 'submitting' ? 'Submitting…' : 'Update Player'}
          </SubmitButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default AmendPlayer;

